import React from 'react';
import { Box, Stack, Typography, Skeleton } from '@compass/core';
import { StudentDataProvider, useStudentData } from '../contexts/StudentDataContext';
import { DashboardCard } from "../components/DashboardCard";
import { StudentFilterControls } from './StudentFilterControls';


const TotalStudentsCardContent = () => {
    const { data, loading, error } = useStudentData();

    if (loading) {
        return <Skeleton variant="rounded" height={300} />;
    }

    if (error) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!data || data.rows.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography>
                    {data?.schoolYearDisplay 
                        ? `No enrollments found for ${data.schoolYearDisplay}` 
                        : 'No enrollment data available'}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Stack 
                direction="row" 
                justifyContent="space-between" 
                alignItems="center"
                sx={{ mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'divider' }}
            >
                <Typography variant="subtitle1" fontWeight="bold">Grade</Typography>
                <Typography variant="subtitle1" fontWeight="bold">{data.schoolYearDisplay}</Typography>
            </Stack>

            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                <Stack direction="column">
                    {data.rows.map((row) => (
                        <Stack 
                            key={row.gradeLevelId}
                            direction="row" 
                            justifyContent="space-between" 
                            alignItems="center"
                            sx={{ 
                                py: 1, 
                                px: 1,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                '&:hover': {
                                    backgroundColor: 'action.hover'
                                }
                            }}
                        >
                            <Typography sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                                {row.gradeLevelName}
                            </Typography>
                            <Typography>{row.studentCount.toLocaleString()}</Typography>
                        </Stack>
                    ))}
                </Stack>
            </Box>
            
            <Stack 
                direction="row" 
                justifyContent="space-between" 
                alignItems="center"
                sx={{ 
                    py: 1.5,
                    px: 1,
                    mt: 2,
                    borderTop: '2px solid',
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(49, 130, 189, 0.15)',
                    borderRadius: '4px'
                }}
            >
                <Typography sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    Total
                </Typography>
                <Typography sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {data.total.toLocaleString()}
                </Typography>
            </Stack>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                    Grade Levels Served
                </Typography>
            </Box>
        </Box>
    );
};

export const TotalStudentsCard = () => {
    return (
        <DashboardCard title="Total Students">
            <StudentDataProvider>
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Filter controls handle the dropdown logic */}
                    <StudentFilterControls />
                    
                    <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                        <TotalStudentsCardContent />
                    </Box>
                </Box>
            </StudentDataProvider>
        </DashboardCard>
    );
};